import { z } from 'zod';

const nullableStringSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? null);

const nullableStringArraySchema = z
  .array(z.string())
  .nullish()
  .transform((value) => value ?? []);

const WizApplicationEndpointHttpResultSchema = z.object({
  statusCode: z
    .int()
    .nullish()
    .transform((value) => value ?? null),
  statusText: nullableStringSchema,
  pageTitle: nullableStringSchema,
  authenticationMethod: nullableStringSchema,
  authenticationServiceProvider: nullableStringSchema,
  contentType: nullableStringSchema,
  screenshotUrl: nullableStringSchema,
});

const WizApplicationEndpointHttpResultsSchema = z
  .union([
    z.array(WizApplicationEndpointHttpResultSchema),
    WizApplicationEndpointHttpResultSchema.transform((value) => [value]),
  ])
  .nullish()
  .transform((value) => value ?? []);

const WizApplicationEndpointCloudAccountSchema = z.object({
  id: nullableStringSchema,
  externalId: nullableStringSchema,
});

const WizApplicationEndpointResourceSchema = z.object({
  id: z.string().min(1),
  name: nullableStringSchema,
  type: nullableStringSchema,
});

const WizApplicationEndpointResourcesSchema = z
  .array(WizApplicationEndpointResourceSchema.nullable())
  .nullish()
  .transform((value) =>
    (value ?? []).filter(
      (
        resource,
      ): resource is z.infer<typeof WizApplicationEndpointResourceSchema> =>
        resource !== null,
    ),
  );

export const WizApplicationEndpointSchema = z.object({
  id: z.string().min(1),
  name: nullableStringSchema,
  host: z.string().min(1),
  port: z
    .int()
    .nullish()
    .transform((value) => value ?? null),
  protocols: nullableStringArraySchema,
  cloudPlatform: nullableStringSchema,
  cloudAccount: WizApplicationEndpointCloudAccountSchema.nullish().transform(
    (value) => value ?? null,
  ),
  firstSeen: nullableStringSchema,
  updatedAt: nullableStringSchema,
  portStatus: nullableStringSchema,
  httpResults: WizApplicationEndpointHttpResultsSchema,
  exposureLevel: nullableStringSchema,
  scanSources: nullableStringArraySchema,
  resources: WizApplicationEndpointResourcesSchema,
});

export const WizApplicationEndpointsConnectionSchema = z.object({
  nodes: z.array(WizApplicationEndpointSchema),
  pageInfo: z.object({
    endCursor: z.string().nullable(),
    hasNextPage: z.boolean(),
  }),
});

export type WizApplicationEndpoint = z.infer<
  typeof WizApplicationEndpointSchema
>;
